import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package, Plus, Search, Minus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { nome: "", codice: "", quantita: 0, scorta_minima: 0, posizione: "" };

export default function Warehouse() {
  const { hasPerm } = useAuth();
  const [parts, setParts] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = async () => { const res = await api.get("/parts"); setParts(res.data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ nome: p.nome, codice: p.codice || "", quantita: p.quantita, scorta_minima: p.scorta_minima, posizione: p.posizione || "" }); setOpen(true); };

  const save = async () => {
    if (!form.nome.trim()) { toast.error("Nome obbligatorio"); return; }
    const payload = { ...form, quantita: parseInt(form.quantita) || 0, scorta_minima: parseInt(form.scorta_minima) || 0 };
    try {
      if (editing) await api.put(`/parts/${editing.id}`, payload);
      else await api.post("/parts", payload);
      toast.success(editing ? "Ricambio aggiornato" : "Ricambio creato");
      setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const adjust = async (p, delta) => {
    try { await api.post(`/parts/${p.id}/stock`, { delta }); await load(); }
    catch (e) { toast.error("Errore"); }
  };

  const del = async (p) => {
    try { await api.delete(`/parts/${p.id}`); toast.success("Eliminato"); await load(); }
    catch (e) { toast.error("Errore"); }
  };

  const filtered = parts.filter((p) => [p.nome, p.codice, p.posizione].join(" ").toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Gestione scorte</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">MAGAZZINO RICAMBI</h1>
        </div>
        {hasPerm("warehouse.create") && (
          <button onClick={openNew} data-testid="add-part-btn"
            className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] transition-colors active:scale-95">
            <Plus className="h-5 w-5" /> Nuovo ricambio
          </button>
        )}
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
        <Input data-testid="warehouse-search" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca ricambio..." className="pl-9 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
      </div>

      {filtered.length === 0 ? (
        <div className="text-zinc-600 text-sm py-16 text-center border border-dashed border-zinc-800 rounded-sm">Nessun ricambio in magazzino.</div>
      ) : (
        <div className="bg-[#141414] border border-zinc-800 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-bold text-zinc-500 uppercase tracking-wider border-b border-zinc-800">
                  <th className="text-left px-4 py-3">Ricambio</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Codice</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Posizione</th>
                  <th className="text-center px-4 py-3">Scorta</th>
                  <th className="text-center px-4 py-3">Stock</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const low = p.quantita <= p.scorta_minima;
                  return (
                    <tr key={p.id} data-testid={`part-row-${p.id}`} className="border-b border-zinc-800/50 hover:bg-[#1a1a1a] transition-colors">
                      <td className="px-4 py-3 font-medium">{p.nome}</td>
                      <td className="px-4 py-3 text-zinc-500 hidden sm:table-cell">{p.codice || "—"}</td>
                      <td className="px-4 py-3 text-zinc-500 hidden md:table-cell">{p.posizione || "—"}</td>
                      <td className="px-4 py-3 text-center text-zinc-500">{p.scorta_minima}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {hasPerm("warehouse.edit") && (
                            <button onClick={() => adjust(p, -1)} data-testid={`stock-minus-${p.id}`} className="h-6 w-6 flex items-center justify-center rounded-sm border border-zinc-700 hover:bg-zinc-800"><Minus className="h-3 w-3" /></button>
                          )}
                          <span className={`font-display text-2xl min-w-[2.5rem] text-center ${low ? "text-[#FF9500]" : "text-white"}`}>{p.quantita}</span>
                          {hasPerm("warehouse.edit") && (
                            <button onClick={() => adjust(p, 1)} data-testid={`stock-plus-${p.id}`} className="h-6 w-6 flex items-center justify-center rounded-sm border border-zinc-700 hover:bg-zinc-800"><Plus className="h-3 w-3" /></button>
                          )}
                        </div>
                        {low && <div className="flex items-center justify-center gap-1 text-[10px] text-[#FF9500] mt-1"><AlertTriangle className="h-3 w-3" /> sotto scorta</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {hasPerm("warehouse.edit") && (
                            <button onClick={() => openEdit(p)} data-testid={`edit-part-${p.id}`} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm"><Pencil className="h-4 w-4" /></button>
                          )}
                          {hasPerm("warehouse.delete") && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button data-testid={`delete-part-${p.id}`} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-[#141414] border-zinc-800">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Eliminare "{p.nome}"?</AlertDialogTitle>
                                  <AlertDialogDescription>Azione irreversibile.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => del(p)} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2f26]">Elimina</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Package className="h-5 w-5 text-[#f98125]" /> {editing ? "Modifica ricambio" : "Nuovo ricambio"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Nome *</Label>
              <Input data-testid="part-form-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. Pastiglie freno Shimano" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Codice</Label>
              <Input data-testid="part-form-codice" value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Posizione</Label>
              <Input data-testid="part-form-posizione" value={form.posizione} onChange={(e) => setForm({ ...form, posizione: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. Scaffale A3" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Quantità</Label>
              <Input type="number" data-testid="part-form-quantita" value={form.quantita} onChange={(e) => setForm({ ...form, quantita: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Scorta minima</Label>
              <Input type="number" data-testid="part-form-scorta" value={form.scorta_minima} onChange={(e) => setForm({ ...form, scorta_minima: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={save} data-testid="part-form-save" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
